import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { StandardIds } from '@galaxy/crm/static';
import { FieldValueInterface } from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { BlankValueComponent, CustomCellComponent } from '@vendasta/galaxy/table';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PartnerCRMSalespersonService, SalespersonWithDisplayName } from '../../salesperson.service';

const NO_VALUE_FOR_SALESPERSON = '<NO_VALUE_FOR_SALESPERSON>';
const AVATAR_WIDTH = 24;

@Component({
  template: `
    <ng-container *ngIf="salesperson() as salesperson; else loading">
      <div class="salesperson" *ngIf="salesperson !== NO_VALUE_FOR_SALESPERSON; else blank">
        <glxy-avatar
          [name]="salespersonName()"
          [width]="AVATAR_WIDTH"
          [src]="salesperson.photoUrlHttps ?? ''"
        ></glxy-avatar>
        <span>{{ salespersonName() }}</span>
      </div>
    </ng-container>
    <ng-template #loading>
      <div class="stencil-shimmer"></div>
    </ng-template>
    <ng-template #blank>
      <glxy-blank-value></glxy-blank-value>
    </ng-template>
  `,
  styleUrls: ['./salesperson.component.scss'],
  imports: [CommonModule, BlankValueComponent, GalaxyAvatarModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalespersonCellComponent extends CustomCellComponent {
  AVATAR_WIDTH = AVATAR_WIDTH;
  NO_VALUE_FOR_SALESPERSON = NO_VALUE_FOR_SALESPERSON;
  fieldValues = signal([]);
  set value(newValues: (FieldValueInterface | null)[]) {
    this.fieldValues.set(newValues);
  }
  salesperson: WritableSignal<SalespersonWithDisplayName | '<NO_VALUE_FOR_SALESPERSON>' | null> = signal(null);
  salespersonName = computed(() => {
    const salesperson = this.salesperson();
    if (salesperson === NO_VALUE_FOR_SALESPERSON) {
      return '';
    }
    return salesperson?.displayName ?? '';
  });

  constructor(private readonly salespersonService: PartnerCRMSalespersonService) {
    super();
    toObservable(this.fieldValues)
      .pipe(
        switchMap((fieldValues) => {
          const salespersonId =
            fieldValues.find(
              (fieldValue) =>
                fieldValue?.fieldId === StandardIds.CompanyPrimarySalespersonID ||
                fieldValue?.fieldId === StandardIds.ContactPrimarySalespersonID,
            ) ?? null;
          if (!salespersonId || !salespersonId?.stringValue) {
            return of(NO_VALUE_FOR_SALESPERSON);
          }
          return this.salespersonService.getMultiSalesperson([salespersonId.stringValue]);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        if (result === NO_VALUE_FOR_SALESPERSON || !result.length) {
          this.salesperson.set(NO_VALUE_FOR_SALESPERSON);
          return;
        }
        const salesperson = result[0] as SalespersonWithDisplayName;
        if (!salesperson?.salespersonId) {
          this.salesperson.set(NO_VALUE_FOR_SALESPERSON);
          return;
        }
        this.salesperson.set(salesperson);
      });
  }
}
